import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Star,
  Loader2,
  AlertCircle,
  Users,
  MessageSquare,
  Inbox,
  Mail,
  UserCheck,
  UserX,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { SiteIcon } from "@/components/icons/siteIcons";
import { iconography } from "@/design-system/tokens/iconography";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { usePingChatsInfinite } from "@/hooks/ping/usePingChats";
import { useRealtimeChats } from "@/hooks/ping/useRealtimeChats";
import { useInternalChats } from "@/hooks/ping/useInternalChats";
import { useRealtimeInternalChats } from "@/hooks/ping/useRealtimeInternalChats";
import { usePingStore } from "@/stores/ping-store";
import { useAuthStore } from "@/stores/authStore";
import { setCurrentViewingChat } from "@/lib/chat-cache-manager";
import { AddPatientChatDialog } from "./AddPatientChatDialog";
import { AddInternalChatDialog } from "./AddInternalChatDialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { pingAPI } from "@/lib/ping-api";
import type { ChatListParams, SupportChat, InternalChat } from "@/types";

// Helper function to format time
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 60) {
    return `${diffMinutes}m`;
  } else if (diffHours < 24) {
    return `${diffHours}h`;
  } else if (diffDays === 1) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  }
};

// Helper function to get user initials
const getUserInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

// Helper function to get avatar treatment from semantic UI tokens
const getAvatarTone = (name: string) => {
  const tones = [
    "border border-primary/15 bg-primary/10 text-primary",
    "border border-border/60 bg-muted text-foreground",
    "border border-border/60 bg-secondary text-secondary-foreground",
    "border border-border/60 bg-accent text-accent-foreground",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return tones[Math.abs(hash) % tones.length];
};

// Debounced search hook for performance
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface ChatListProps {
  activeTab: "patient" | "teams";
  setActiveTab: (tab: "patient" | "teams") => void;
  selectedConversation: string;
  setSelectedConversation: (id: string, isInternal?: boolean) => void;
}

export const ChatList = React.memo(
  ({
    activeTab,
    setActiveTab,
    selectedConversation,
    setSelectedConversation,
  }: ChatListProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    // Add refresh interval state
    const [isViewActive, setIsViewActive] = useState(true);

    // Filter dropdown state
    const [filterOption, setFilterOption] = useState<
      | "all"
      | "unread"
      | "starred"
      | "unassigned"
      | "assigned"
      | "assigned_to_me"
      | "open"
      | "resolved"
      | "closed"
      | "is_unread"
    >("all");

    // Dialog state
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const { user, isStaffAdmin } = useAuthStore();
    const {
      setSelectedChat,
      patientUnreadCount,
      teamsUnreadCount,
      setPatientUnreadCount,
      setTeamsUnreadCount,
      markPatientTabAsChecked,
      markTeamsTabAsChecked,
    } = usePingStore();
    const queryClient = useQueryClient();

    const supportChatParams = useMemo<ChatListParams>(() => {
      const params: ChatListParams = {
        patient_name:
          activeTab === "patient"
            ? debouncedSearchQuery || undefined
            : undefined,
        page_size: 50,
      };

      if (activeTab !== "patient") return params;

      switch (filterOption) {
        case "unread":
          params.has_unread = true;
          break;
        case "starred":
          params.is_starred = true;
          break;
        case "unassigned":
          params.unassigned = true;
          break;
        case "assigned":
          params.assigned = true;
          break;
        case "assigned_to_me":
          params.assigned_to_me = true;
          break;
        case "is_unread":
          params.is_unread = true;
          params.assigned_to_me = true;
          break;
        case "open":
        case "resolved":
        case "closed":
          params.status = filterOption;
          break;
      }

      return params;
    }, [activeTab, debouncedSearchQuery, filterOption]);

    // Fetch support chats (for patient tab)
    const {
      data: supportChatsData,
      isLoading: isLoadingSupport,
      error: supportError,
      refetch: refetchSupport,
      fetchNextPage: fetchNextSupportPage,
      hasNextPage: hasNextSupportPage,
      isFetchingNextPage: isFetchingNextSupportPage,
    } = usePingChatsInfinite(supportChatParams, true);

    const supportChats = useMemo(
      () => supportChatsData?.pages.flatMap((page) => page.results) || [],
      [supportChatsData]
    );

    // Patient tab unread badge count — must reflect total unread regardless
    // of the visible filter, so we read `count` off a dedicated paginated
    // query (no dedicated count endpoint exists). Kept global (not scoped
    // to assigned_to_me) to match prior behaviour for staff_admin views.
    const unreadBadgeParams: ChatListParams = {
      has_unread: true,
      page_size: 1,
    };
    const { data: unreadBadgePage } = useQuery({
      queryKey: queryKeys.ping.chats.list(unreadBadgeParams),
      queryFn: () => pingAPI.chats.list(unreadBadgeParams),
      enabled: !!user,
      staleTime: Infinity,
    });
    const patientUnreadServerCount = unreadBadgePage?.count ?? 0;

    // Fetch internal chats (for teams tab)
    const {
      data: internalChats = [],
      isLoading: isLoadingInternal,
      error: internalError,
      refetch: refetchInternal,
    } = useInternalChats(
      {
        search:
          activeTab === "teams" ? debouncedSearchQuery || undefined : undefined,
        page: 1,
        page_size: 50,
      },
      true
    );

    // Conditional data based on active tab
    const chats = activeTab === "teams" ? internalChats : supportChats;
    const isLoading =
      activeTab === "teams" ? isLoadingInternal : isLoadingSupport;
    const error =
      activeTab === "teams"
        ? internalError && (internalError as any)?.status !== 404
          ? internalError
          : null
        : supportError && (supportError as any)?.status !== 404
          ? supportError
          : null;
    const refetch = activeTab === "teams" ? refetchInternal : refetchSupport;

    // Set up real-time subscriptions
    const {
      isConnected: isRealtimeConnected,
      connectionState,
      activeSubscriptions,
      markCurrentChatAsRead,
    } = useRealtimeChats(supportChats, activeTab !== "patient");

    const {
      isConnected: isInternalRealtimeConnected,
      markCurrentChatAsRead: markInternalChatAsRead,
    } = useRealtimeInternalChats(internalChats, activeTab !== "teams");

    // Handle view lifecycle changes
    useEffect(() => {
      const handleVisibilityChange = () => {
        setIsViewActive(!document.hidden);
      };

      const handleFocus = () => setIsViewActive(true);
      const handleBlur = () => setIsViewActive(false);

      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("focus", handleFocus);
      window.addEventListener("blur", handleBlur);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        window.removeEventListener("focus", handleFocus);
        window.removeEventListener("blur", handleBlur);
      };
    }, []);

    // Filter chats based on active tab and user role
    const filteredChats = useMemo(() => {
      if (activeTab === "teams") {
        const base = (chats as InternalChat[]).filter((chat: InternalChat) => {
          switch (filterOption) {
            case "all":
              return true;
            case "unread":
              return chat.unread_count > 0;
            case "starred":
              return !!chat.is_starred;
            default:
              return true;
          }
        });

        return [...base].sort((a: InternalChat, b: InternalChat) => {
          const starA = a.is_starred ? 1 : 0;
          const starB = b.is_starred ? 1 : 0;
          if (starA !== starB) return starB - starA;
          const aTime = new Date(a.last_message_at || a.created_at).getTime();
          const bTime = new Date(b.last_message_at || b.created_at).getTime();
          return bTime - aTime;
        });
      } else {
        // Patient (support) chats are filtered server-side via
        // `supportChatParams` (ZCS-304); the local list is the filtered
        // set already, so just sort.
        const base = chats as SupportChat[];

        return [...base].sort((a: SupportChat, b: SupportChat) => {
          const starA = a.is_starred ? 1 : 0;
          const starB = b.is_starred ? 1 : 0;
          if (starA !== starB) return starB - starA;
          const aTime = new Date(
            a.last_user_message_at ||
              a.last_care_manager_message_at ||
              a.created_at
          ).getTime();
          const bTime = new Date(
            b.last_user_message_at ||
              b.last_care_manager_message_at ||
              b.created_at
          ).getTime();
          return bTime - aTime;
        });
      }
    }, [chats, activeTab, filterOption]);

    // Calculate unread counts
    const chatsWithOptimizedUnreadCount = useMemo(() => {
      if (activeTab === "teams") {
        return (filteredChats as InternalChat[]).map((chat: InternalChat) => ({
          ...chat,
          unread_count:
            chat.id === selectedConversation ? 0 : chat.unread_count,
        }));
      } else {
        return (filteredChats as SupportChat[]).map((chat: SupportChat) => ({
          ...chat,
          unread_count:
            chat.id === selectedConversation ? 0 : chat.unread_count,
        }));
      }
    }, [filteredChats, selectedConversation, activeTab]) as (
      | SupportChat
      | InternalChat
    )[];

    const currentTabUnreadCount = chatsWithOptimizedUnreadCount.filter(
      (chat: SupportChat | InternalChat) => chat.unread_count > 0
    ).length;

    const previousTabRef = React.useRef<"patient" | "teams">(activeTab);
    const hasJustSwitchedRef = React.useRef(false);

    useEffect(() => {
      const previousTab = previousTabRef.current;

      if (previousTab !== activeTab) {
        hasJustSwitchedRef.current = true;
        previousTabRef.current = activeTab;

        if (activeTab === "patient") {
          markPatientTabAsChecked();
        } else if (activeTab === "teams") {
          markTeamsTabAsChecked();
        }
      }
    }, [activeTab, markPatientTabAsChecked, markTeamsTabAsChecked]);

    useEffect(() => {
      if (
        activeTab === "teams" &&
        !["all", "unread", "starred"].includes(filterOption)
      ) {
        setFilterOption("all");
      }
    }, [activeTab, filterOption]);

    useEffect(() => {
      if (isLoading && hasJustSwitchedRef.current) {
        return;
      }

      if (hasJustSwitchedRef.current && !isLoading) {
        hasJustSwitchedRef.current = false;
      }

      if (activeTab === "teams" && !isLoading) {
        setTeamsUnreadCount(currentTabUnreadCount);
      }
    }, [activeTab, currentTabUnreadCount, isLoading, setTeamsUnreadCount]);

    // Patient unread badge is driven by the dedicated server count so it
    // stays correct regardless of the visible filter (ZCS-304). Runs
    // independently of the active tab so the badge updates even when the
    // user is on Teams.
    useEffect(() => {
      setPatientUnreadCount(patientUnreadServerCount);
    }, [patientUnreadServerCount, setPatientUnreadCount]);

    // Handle chat selection
    const handleChatSelect = useCallback(
      (chatId: string, isInternal: boolean = false) => {
        setSelectedConversation(chatId, isInternal);
        setSelectedChat(chatId);

        if (!isInternal) {
          setCurrentViewingChat(chatId);
        }

        if (isInternal) {
          queryClient.setQueriesData(
            { queryKey: queryKeys.ping.internalChats.all() },
            (oldData: any) => {
              if (!oldData?.results) return oldData;
              return {
                ...oldData,
                results: oldData.results.map((chat: any) =>
                  chat.id === chatId ? { ...chat, unread_count: 0 } : chat
                ),
              };
            }
          );
          setTimeout(() => {
            markInternalChatAsRead();
          }, 100);
        } else {
          queryClient.setQueriesData(
            { queryKey: queryKeys.ping.chats.all() },
            (oldData: any) => {
              if (oldData?.pages) {
                return {
                  ...oldData,
                  pages: oldData.pages.map((page: any) => ({
                    ...page,
                    results: page.results.map((chat: any) =>
                      chat.id === chatId ? { ...chat, unread_count: 0 } : chat
                    ),
                  })),
                };
              }
              if (!oldData?.results) return oldData;
              return {
                ...oldData,
                results: oldData.results.map((chat: any) =>
                  chat.id === chatId ? { ...chat, unread_count: 0 } : chat
                ),
              };
            }
          );
          setTimeout(() => {
            markCurrentChatAsRead();
          }, 100);
        }
      },
      [
        setSelectedConversation,
        setSelectedChat,
        markCurrentChatAsRead,
        markInternalChatAsRead,
        queryClient,
      ]
    );

    const handleOpenDialog = useCallback(() => {
      setIsDialogOpen(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
      setIsDialogOpen(false);
    }, []);

    const handlePatientSelect = useCallback(
      async (chatId: string) => {
        setSelectedConversation(chatId);
        await refetchSupport();
      },
      [setSelectedConversation, refetchSupport]
    );

    const handleInternalChatSelect = useCallback(
      async (chatId: string) => {
        setSelectedConversation(chatId, true);
        await refetchInternal();
      },
      [setSelectedConversation, refetchInternal]
    );

    // Get filter icon and label
    const getFilterDisplay = () => {
      const icons = {
        all: <Inbox className="h-4 w-4" />,
        unread: <Mail className="h-4 w-4" />,
        starred: <Star className="h-4 w-4" />,
        unassigned: <UserX className="h-4 w-4" />,
        assigned: <Users className="h-4 w-4" />,
        assigned_to_me: <UserCheck className="h-4 w-4" />,
        is_unread: <Mail className="h-4 w-4" />,
        open: <MessageSquare className="h-4 w-4" />,
        resolved: <CheckCircle2 className="h-4 w-4" />,
        closed: <Circle className="h-4 w-4" />,
      };

      const labels: Record<string, string> = {
        all: activeTab === "teams" ? "All Chats" : "All Pings",
        unread: "Unread",
        starred: "Starred",
        unassigned: "Unassigned",
        assigned: "Assigned",
        assigned_to_me: "Assigned to me",
        is_unread: "Unread",
        open: "Open",
        resolved: "Resolved",
        closed: "Closed",
      };

      return { icon: icons[filterOption], label: labels[filterOption] };
    };

    const { icon: filterIcon, label: filterLabel } = getFilterDisplay();

    return (
      <Card className="flex min-h-0 w-96 max-w-full flex-col overflow-hidden rounded-3xl border-border/60 gap-0 py-0">
        {/* Header */}
        <div className="border-b border-border p-6 pb-4">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="z-h2 text-foreground">Inbox</h1>
              <p className="z-secondary text-muted-foreground">
                {activeTab === "teams"
                  ? "Team conversations"
                  : "Patient messages"}
              </p>
            </div>
            <Button
              size="icon"
              className="h-9 w-9 shrink-0 rounded-md"
              onClick={handleOpenDialog}
              aria-label="Create new chat"
            >
              <SiteIcon name="plus" className={iconography.size.inline} />
            </Button>
          </div>

          <div className="mb-4">
            <Select
              value={filterOption}
              onValueChange={(value) =>
                setFilterOption(
                  value as
                    | "all"
                    | "unread"
                    | "starred"
                    | "unassigned"
                    | "assigned"
                    | "assigned_to_me"
                    | "open"
                    | "resolved"
                    | "closed"
                    | "is_unread"
                )
              }
            >
              <SelectTrigger className="z-button relative h-10 w-full rounded-full border-0 bg-muted pl-11 pr-10 shadow-none hover:bg-muted focus:ring-2 focus:ring-ring focus:ring-offset-0">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground">
                  {filterIcon}
                </span>
                <SelectValue aria-label={filterLabel}>
                  {filterLabel}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center gap-2">
                    <Inbox className="h-4 w-4" />
                    <span>
                      {activeTab === "teams" ? "All Chats" : "All Pings"}
                    </span>
                  </span>
                </SelectItem>
                <SelectItem value="unread">
                  <span className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>Unread</span>
                  </span>
                </SelectItem>
                <SelectItem value="starred">
                  <span className="flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    <span>Starred</span>
                  </span>
                </SelectItem>
                {activeTab === "patient" && (
                  <>
                    <SelectItem value="unassigned">
                      <span className="flex items-center gap-2">
                        <UserX className="h-4 w-4" />
                        <span>Unassigned</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="assigned">
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>Assigned</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="assigned_to_me">
                      <span className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4" />
                        <span>Assigned to me</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="open">
                      <span className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        <span>Open</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="resolved">
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Resolved</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="closed">
                      <span className="flex items-center gap-2">
                        <Circle className="h-4 w-4" />
                        <span>Closed</span>
                      </span>
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as "patient" | "teams")
            }
          >
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="patient" className="z-secondary gap-2">
                <span>Patient</span>
                {patientUnreadCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="z-caption tabular-nums h-5 min-w-5 px-1"
                  >
                    {patientUnreadCount > 99 ? "99+" : patientUnreadCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="teams" className="z-secondary gap-2">
                <span>Teams</span>
                {teamsUnreadCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="z-caption tabular-nums h-5 min-w-5 px-1"
                  >
                    {teamsUnreadCount > 99 ? "99+" : teamsUnreadCount}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Search */}
        <div className="border-b border-border px-6 py-3">
          <SearchInput
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
          />
        </div>

        {/* Conversation List */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-6 space-y-3">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="z-body text-muted-foreground">
                Loading chats...
              </span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center p-6 space-y-3">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <span className="z-body text-center text-destructive">
                Failed to load chats
              </span>
              <Button
                variant="outline"
                size="sm"
                className="z-button"
                onClick={() => refetch()}
              >
                Try Again
              </Button>
            </div>
          ) : chatsWithOptimizedUnreadCount.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-3">
                <Inbox className="w-8 h-8 text-muted-foreground/50" />
              </div>
              <p className="z-body-md mb-1 text-foreground">
                {debouncedSearchQuery ? "No chats found" : "No chats yet"}
              </p>
              <p className="z-caption text-muted-foreground">
                {debouncedSearchQuery
                  ? "Try a different search term"
                  : "Start a new conversation to get started"}
              </p>
            </div>
          ) : (
            <>
              {chatsWithOptimizedUnreadCount.map(
                (chat: SupportChat | InternalChat) => {
                  if (activeTab === "teams") {
                    const internalChat = chat as InternalChat;
                    const participantName =
                      internalChat.other_participant.full_name ||
                      `${internalChat.other_participant.first_name} ${internalChat.other_participant.last_name}`;
                    const initials = getUserInitials(
                      internalChat.other_participant.first_name,
                      internalChat.other_participant.last_name
                    );
                    const lastMessageTime =
                      internalChat.last_message?.created_at ||
                      internalChat.last_message_at ||
                      internalChat.created_at;

                    let lastMessageText = "No messages yet";
                    if (internalChat.last_message) {
                      if (internalChat.last_message.text_content) {
                        lastMessageText =
                          internalChat.last_message.text_content;
                      } else if (
                        internalChat.last_message.message_type === "image"
                      ) {
                        lastMessageText = "📷 Image";
                      } else if (
                        internalChat.last_message.message_type === "document"
                      ) {
                        lastMessageText = "📎 Document";
                      }
                    }

                    return (
                      <div
                        key={internalChat.id}
                        className={cn(
                          "cursor-pointer border-b border-border/50 px-4 py-3 transition-colors",
                          "hover:bg-muted/50",
                          selectedConversation === internalChat.id &&
                            "bg-primary/5 border-l-2 border-l-primary"
                        )}
                        onClick={() => handleChatSelect(internalChat.id, true)}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              "z-secondary flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                              getAvatarTone(participantName)
                            )}
                          >
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="z-body-md truncate text-foreground">
                                {participantName}
                              </h3>
                              <div className="flex items-center gap-1 shrink-0">
                                <span className="z-caption tabular-nums text-muted-foreground">
                                  {formatTime(lastMessageTime)}
                                </span>
                                {internalChat.is_starred && (
                                  <Star className="h-3.5 w-3.5 text-warning fill-warning" />
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-0.5 gap-2">
                              <p className="z-body truncate text-muted-foreground">
                                {lastMessageText}
                              </p>
                              {internalChat.unread_count > 0 && (
                                <Badge className="z-caption tabular-nums h-5 min-w-5 shrink-0 bg-primary px-1.5 text-primary-foreground hover:bg-primary">
                                  {internalChat.unread_count}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    const supportChat = chat as SupportChat;
                    const patientName = `${supportChat.user.first_name} ${supportChat.user.last_name}`;
                    const initials = getUserInitials(
                      supportChat.user.first_name,
                      supportChat.user.last_name
                    );
                    const lastMessageTime =
                      supportChat.last_message?.created_at ||
                      supportChat.last_user_message_at ||
                      supportChat.last_care_manager_message_at ||
                      supportChat.created_at;

                    let lastMessageText = "No messages yet";
                    if (supportChat.last_message) {
                      if (supportChat.last_message.text_content) {
                        lastMessageText = supportChat.last_message.text_content;
                      } else if (
                        supportChat.last_message.message_type === "image"
                      ) {
                        lastMessageText = "📷 Image";
                      } else if (
                        supportChat.last_message.message_type === "document"
                      ) {
                        lastMessageText = "📎 Document";
                      }
                    }

                    return (
                      <div
                        key={supportChat.id}
                        className={cn(
                          "cursor-pointer border-b border-border/50 px-4 py-3 transition-colors",
                          "hover:bg-muted/50",
                          selectedConversation === supportChat.id &&
                            "bg-primary/5 border-l-2 border-l-primary"
                        )}
                        onClick={() => handleChatSelect(supportChat.id, false)}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              "z-secondary flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                              getAvatarTone(patientName)
                            )}
                          >
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="z-body-md truncate text-foreground">
                                {patientName}
                              </h3>
                              <div className="flex items-center gap-1 shrink-0">
                                <span className="z-caption tabular-nums text-muted-foreground">
                                  {formatTime(lastMessageTime)}
                                </span>
                                {supportChat.is_starred &&
                                  (supportChat.care_manager === user?.id ||
                                    isStaffAdmin()) && (
                                    <Star className="h-3.5 w-3.5 text-warning fill-warning" />
                                  )}
                                {supportChat.is_unread &&
                                  (supportChat.care_manager === user?.id ||
                                    isStaffAdmin()) && (
                                    <div className="h-2 w-2 rounded-full bg-success" />
                                  )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-0.5 gap-2">
                              <p className="z-body truncate text-muted-foreground">
                                {lastMessageText}
                              </p>
                              {supportChat.unread_count > 0 && (
                                <Badge className="z-caption tabular-nums h-5 min-w-5 shrink-0 bg-primary px-1.5 text-primary-foreground hover:bg-primary">
                                  {supportChat.unread_count}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                }
              )}
              {activeTab === "patient" && hasNextSupportPage && (
                <div className="border-b border-border/50 p-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="z-button w-full"
                    disabled={isFetchingNextSupportPage}
                    onClick={() => fetchNextSupportPage()}
                  >
                    {isFetchingNextSupportPage ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      "Load more"
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Add Patient Chat Dialog */}
        <AddPatientChatDialog
          isOpen={isDialogOpen && activeTab === "patient"}
          onClose={handleCloseDialog}
          onPatientSelect={handlePatientSelect}
        />

        {/* Add Internal Chat Dialog */}
        <AddInternalChatDialog
          isOpen={isDialogOpen && activeTab === "teams"}
          onClose={handleCloseDialog}
          onChatSelect={handleInternalChatSelect}
        />
      </Card>
    );
  }
);

ChatList.displayName = "ChatList";
